import { Node, mergeAttributes } from "@tiptap/core";

// Wraps MathLive's <math-field> custom element as an atomic, read-only Tiptap Node.
// Deliberately uses a NodeView (raw DOM control) rather than any HTML-string insertion path —
// that's the whole point: today's bug was execCommand("insertHTML") silently failing to run
// custom-element upgrade reactions. A NodeView never goes through execCommand at all, and
// Tiptap's own HTML parsing (setContent/parseHTML) goes through the browser's real DOMParser,
// which upgrades custom elements normally. See the plan's §3 for the full write-up.
export const MathField = Node.create({
  name: "mathField",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      latex: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-latex") || "",
        renderHTML: (attrs: { latex: string }) => ({ "data-latex": attrs.latex }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "math-field[data-latex]" }];
  },

  renderHTML({ HTMLAttributes }) {
    // Must match today's saved shape exactly: <math-field data-latex="…" read-only
    // contenteditable="false"></math-field> — RichContentDisplay.razor's hydrateStatic() and
    // QuestionContentRenderer.cs's sanitizer allow-list both already expect this precise shape,
    // and neither changes as part of this migration.
    // No trailing "0" content-hole here — atom nodes have no `content` expression, so a content
    // hole in the output spec is invalid ("Content hole not allowed in a leaf node spec").
    return ["math-field", mergeAttributes(HTMLAttributes, { "read-only": "", contenteditable: "false" })];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("math-field");
      dom.setAttribute("data-latex", node.attrs.latex);
      dom.setAttribute("read-only", "");
      dom.setAttribute("contenteditable", "false");

      const hydrate = (latex: string) => {
        customElements.whenDefined("math-field").then(() => {
          (dom as any).value = latex;
          (dom as any).readOnly = true;
        });
      };
      hydrate(node.attrs.latex);

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== "mathField") return false;
          if (updatedNode.attrs.latex !== dom.getAttribute("data-latex")) {
            dom.setAttribute("data-latex", updatedNode.attrs.latex);
            hydrate(updatedNode.attrs.latex);
          }
          return true;
        },
      };
    };
  },
});
